import Highcharts from "highcharts";
import { toSafeNumber } from "../data/transformers";
import { TIERED_BAR_CONFIG } from "src/config/charts/tieredBarChartConfig";
import { getTierStyle } from "src/charts/types";

type TieredBarRecord = {
  prescriber?: string;
  doctor_id?: string;
  pts?: number | string;
  pa_cases?: number | string;
  pa_approved_cases?: number | string;
  pa_abandoned_cases?: number | string;
  pa_percentage?: string;
  abandon_percentage?: string;
  tier?: string;
};

type TieredBarData = {
  records?: TieredBarRecord[];
  tier_rules?: Record<string, string>;
};

const TIER_ORDER = ["Performing", "Steady", "Priority Focus"];

const toTierKey = (tier: string): string =>
  tier.toLowerCase().replace(/[\s-]+/g, "_");

const buildSeriesName = (
  tier: string,
  tierRules: Record<string, string>,
): string => {
  const key = toTierKey(tier);
  const rule = tierRules[key];
  return `${tier} (${rule})`;
};

export function renderTieredBar(
  data: unknown,
  spec: unknown,
  title: string,
): Highcharts.Options {
  const tieredData = Array.isArray(data)
    ? { records: data }
    : (data as TieredBarData);

  const records = [...(tieredData.records ?? [])].sort(
    (a, b) => toSafeNumber(b.pts) - toSafeNumber(a.pts),
  );

  const tierRules = tieredData.tier_rules ?? {};

  const categories = records.map((r) => r.prescriber ?? "Unknown");
  const pointCount = records.length;
  const barHeight = pointCount <= 10 ? 20 : pointCount <= 20 ? 16 : 12;

  const series: Highcharts.SeriesOptionsType[] = TIER_ORDER.map((tier) => {
    const color = getTierStyle(tier).dot;

    return {
      type: "bar",
      name: buildSeriesName(tier, tierRules),
      color,
      data: records.map((r) => {
        if (r.tier === tier) {
          return {
            y: toSafeNumber(r.pts),
            custom: {
              prescriber: r.prescriber ?? "",
              pa_cases: toSafeNumber(r.pa_cases),
              pa_approved: toSafeNumber(r.pa_approved_cases),
              pa_percentage: r.pa_percentage ?? "—",
              abandon_percentage: r.abandon_percentage ?? "—",
              tier,
            },
          };
        }
        return null;
      }),
    } as Highcharts.SeriesBarOptions;
  });

  return {
    ...TIERED_BAR_CONFIG,
    chart: {
      ...TIERED_BAR_CONFIG.chart,
      ...(pointCount > 15
        ? {
            scrollablePlotArea: {
              minHeight: pointCount * (barHeight + 10) + 120,
              scrollPositionY: 0,
            },
          }
        : {}),
    },
    // title: { text: title, align: "left" },
    xAxis: {
      categories,
      title: { text: null },
      labels: { style: { fontSize: "11px" } },
    },
    yAxis: {
      min: 0,
      title: { text: "Total Cases" },
      labels: { style: { fontSize: "11px" } },
    },
    plotOptions: {
      bar: {
        grouping: false,
        borderWidth: 0,
        pointWidth: barHeight,
      },
      series: {
        borderWidth: 0,
        dataLabels: { enabled: false },
      },
    },
    tooltip: {
      useHTML: true,
      formatter: function (this: any) {
        const c = this.point?.options?.custom ?? {};
        return `<div style="padding:8px 10px;min-width:210px;">
          <div style="font-size:12px;font-weight:700;margin-bottom:6px;">${c.prescriber}</div>
          <div style="font-size:12px;margin-top:3px;">Total Cases: <b>${this.y}</b></div>
          <div style="font-size:12px;margin-top:3px;">PA Cases: <b>${c.pa_cases}</b></div>
          <div style="font-size:12px;margin-top:3px;">PA Approved: <b> ${c.pa_percentage}</b></div>
          <div style="font-size:12px;margin-top:3px;">Abandon Rate: <b>${c.abandon_percentage}</b></div>
          <div style="margin-top:6px;font-size:11px;"><span style="color:${this.color};">●</span> ${c.tier}</div>
        </div>`;
      },
    },
    series,
  };
}
